import { Outlet as TanstackOutlet, useRouterState } from '@tanstack/react-router';
import { forwardRef, useEffect, useRef } from 'react';

import { useLocation, useNavigate, useResolveTo } from './hooks';
import { OutletContext } from './outlet-context';
import { createPath } from './path-utils';

import type { To } from './path-utils';
import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';

function isModifiedEvent(event: MouseEvent): boolean {
  return !!(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

function shouldProcessLinkClick(event: MouseEvent, target?: string): boolean {
  return event.button === 0 && (!target || target === '_self') && !isModifiedEvent(event);
}

type BaseLinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  to: To;
  replace?: boolean;
  state?: unknown;
  reloadDocument?: boolean;
  preventScrollReset?: boolean;
  relative?: 'route' | 'path';
};

/** React-Router-shaped `<Link>`. Renders an anchor and navigates on click. */
export const Link = forwardRef<HTMLAnchorElement, BaseLinkProps>(function Link(
  { to, replace, state, target, onClick, reloadDocument, preventScrollReset, relative, ...rest },
  ref,
) {
  const navigate = useNavigate();
  const resolveTo = useResolveTo();
  const href = createPath(resolveTo(to));

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (!event.defaultPrevented && !reloadDocument && shouldProcessLinkClick(event, target ?? undefined)) {
      event.preventDefault();
      navigate(to, { replace, state, preventScrollReset });
    }
  };

  // The children arrive through `rest`, so the rule cannot see them.
  // eslint-disable-next-line jsx-a11y/anchor-has-content -- children arrive through rest, so the rule cannot see them
  return <a {...rest} href={href} target={target} onClick={handleClick} ref={ref} />;
});

type NavLinkRenderProps = { isActive: boolean; isPending: boolean; isTransitioning: boolean };

type NavLinkProps = Omit<BaseLinkProps, 'className' | 'style' | 'children'> & {
  end?: boolean;
  caseSensitive?: boolean;
  className?: string | ((props: NavLinkRenderProps) => string | undefined);
  style?: React.CSSProperties | ((props: NavLinkRenderProps) => React.CSSProperties | undefined);
  children?: ReactNode | ((props: NavLinkRenderProps) => ReactNode);
};

/** React-Router-shaped `<NavLink>` — adds `isActive` to `className` / `style` / `children`. */
export const NavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(function NavLink(
  { to, end = false, caseSensitive = false, className, style, children, ...rest },
  ref,
) {
  const { pathname: locationPathname } = useLocation();
  const resolveTo = useResolveTo();
  // Resolve the target against the route context, but compare active state
  // against the full browser location (React Router's NavLink semantics).
  const toPathname = resolveTo(to).pathname;

  const normalizedLocation = caseSensitive ? locationPathname : locationPathname.toLowerCase();
  const normalizedTo = caseSensitive ? toPathname : toPathname.toLowerCase();

  const isActive =
    normalizedLocation === normalizedTo ||
    (!end && normalizedLocation.startsWith(normalizedTo) && normalizedLocation.charAt(normalizedTo.length) === '/');

  const renderProps: NavLinkRenderProps = { isActive, isPending: false, isTransitioning: false };

  const resolvedClassName = typeof className === 'function' ? className(renderProps) : className;
  const resolvedStyle = typeof style === 'function' ? style(renderProps) : style;
  const resolvedChildren = typeof children === 'function' ? children(renderProps) : children;

  return (
    <Link
      {...rest}
      to={to}
      ref={ref}
      className={resolvedClassName}
      style={resolvedStyle}
      aria-current={isActive ? 'page' : undefined}
    >
      {resolvedChildren}
    </Link>
  );
});

type NavigateProps = {
  to: To;
  replace?: boolean;
  state?: unknown;
};

/** React-Router-shaped `<Navigate>` — imperatively navigates to `to`, and
 * re-navigates whenever `to` / `replace` / `state` change (e.g. a redirect whose
 * target is only known after data loads). Keyed on the destination rather than
 * fired once per mount, so it doesn't stack history entries on unrelated
 * re-renders. `navigate` is read through a ref so the effect doesn't re-fire when
 * the shim's `navigate` identity changes (it tracks `location.pathname`, unlike
 * React Router's stable one). */
export function Navigate({ to, replace, state }: NavigateProps): null {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const toKey = typeof to === 'string' ? to : JSON.stringify(to);
  const stateKey = JSON.stringify(state ?? null);

  useEffect(() => {
    navigateRef.current(to, { replace, state });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- toKey/stateKey capture to/state content; navigate is held in a ref
  }, [toKey, replace, stateKey]);

  return null;
}

/** React-Router-shaped `<Outlet>`. Optionally provides `context` to `useOutletContext`. */
export function Outlet({ context }: { context?: unknown }): ReactNode {
  // Rendered outside any matched route (e.g. a layout component mounted directly
  // through the test context wrapper), there's nothing to render — match React
  // Router, which renders nothing rather than throwing.
  const hasMatch = useRouterState({ select: state => state.matches.length > 0 });
  if (!hasMatch) {
    return null;
  }

  if (context !== undefined) {
    return (
      <OutletContext.Provider value={context}>
        <TanstackOutlet />
      </OutletContext.Provider>
    );
  }

  return <TanstackOutlet />;
}
